import { index, integer, pgEnum, pgTable, text, timestamp } from "drizzle-orm/pg-core";

import { productFetchVendorEnum } from "./product-fetch";

/**
 * How one promotion of a cached fetch ended: PROMOTED, or the mapper refusal that stopped it.
 * Only the refusals an unreviewed product can be expected to hit are here; any other failure
 * (a database error, a payload that drifted) writes no attempt row, so the next run tries again.
 */
export enum ProductPromotionOutcome {
  PROMOTED = "PROMOTED",
  GRADE_TIERS_UNSUPPORTED = "GRADE_TIERS_UNSUPPORTED",
  NO_PRICEABLE_VARIANTS = "NO_PRICEABLE_VARIANTS",
  CATEGORY_UNSUPPORTED = "CATEGORY_UNSUPPORTED",
  NOT_PROMOTABLE = "NOT_PROMOTABLE",
}

export const productPromotionOutcomeEnum = pgEnum("product_promotion_outcome", [
  ProductPromotionOutcome.PROMOTED,
  ProductPromotionOutcome.GRADE_TIERS_UNSUPPORTED,
  ProductPromotionOutcome.NO_PRICEABLE_VARIANTS,
  ProductPromotionOutcome.CATEGORY_UNSUPPORTED,
  ProductPromotionOutcome.NOT_PROMOTABLE,
]);

/**
 * One try at turning a cached product fetch into listings, append-only. Writer: the promote
 * path (`fulfillment-core/src/product-fetch/promote.ts`), called by the fetch worker, the
 * sourcingcron and the console's promote button; not tenant-scoped, and nothing reaches a
 * tenant-facing response.
 *
 * The sourcingcron reads the trailing rows of a product to decide when a refused product is
 * due again (`retryAfter` in `fulfillment-core/src/product-fetch/promotion-attempts.ts`). A
 * deferral delays a product and never bans it, so this is no skip list. One promote call writes one row per sourcingrule that asked for the
 * product, all stamped with the same `attempted_at`, so a reader counting repeats groups by it.
 */
export const productPromotionAttempts = pgTable(
  "product_promotion_attempts",
  {
    id: text("id").primaryKey(),
    vendor: productFetchVendorEnum("vendor").notNull(),
    /** The vendor's own product key, as on `product_fetches.vendor_product_key`. */
    externalId: text("external_id").notNull(),
    /** The sourcingrule that asked; null for a promote from the console. No FK. */
    ruleId: text("rule_id"),
    /** The product's 1-based position on that rule's page; null for a console promote. */
    pagePosition: integer("page_position"),
    attemptedAt: timestamp("attempted_at", { withTimezone: true }).notNull(),
    outcome: productPromotionOutcomeEnum("outcome").notNull(),
    /** Distinct owners (tenants, or the shared catalog) that got a row; 0 for a refusal. */
    tenantsWritten: integer("tenants_written").notNull(),
  },
  (t) => [
    index("product_promotion_attempts_product_idx").on(
      t.vendor,
      t.externalId,
      t.attemptedAt.desc(),
    ),
  ],
);
